import PropTypes from "prop-types";
import React, { Suspense } from "react";
import { SpinningLoader, ToggleSwitch } from "risa-oasis-ui_v2";
import { TableCellType } from "../table";
import AddKeyButtonCell from "./cellTypes/AddKeyButtonCell";
import BadgeButton from "./cellTypes/BadgeButton";
import BadgeCell from "./cellTypes/BadgeCell";
import ButtonLinkCell from "./cellTypes/ButtonLinkCell";
import ButtonWithThreeDotsCell from "./cellTypes/ButtonWithThreeDotsCell";
import CheckboxCell from "./cellTypes/CheckboxCell";
import CallingResolvedPhoneCell from "./cellTypes/CallingResolvedPhoneCell";
import CmmInputViewIconCell from "./cellTypes/CmmInputViewIconCell";
import CopyDataCell from "./cellTypes/CopyDataCell";
import CopyIconCell from "./cellTypes/CopyIconCell";
import DateCell from "./cellTypes/DateCell";
import DotsWithPopupCell from "./cellTypes/DotsWithPopupCell";
import DownloadIconCell from "./cellTypes/DownloadIconCell";
import EditableTextCellComponent from "./cellTypes/EditableTextCell";
import ExpandableRowIconCell from "./cellTypes/ExpandableRowIconCell";
import MultilineCell from "./cellTypes/MultilineCell";
import NotesIconCell from "./cellTypes/NotesIconCell";
import NycbsStatusCell from "./cellTypes/NycbsStatusCell";

import { BadgeSelectCell } from "./cellTypes";
import BadgeGroupWithEditIconCell from "./cellTypes/BadgeGroupWithEditIconCell";
import { ColoredTextCell } from "./cellTypes/ColoredTextCell";
import EditableTagListCell from "./cellTypes/EditableTagListCell";
import EditableTextWithIconCell from "./cellTypes/EditableTextWithIconCell";
import InlineTextInputCell from "./cellTypes/InlineTextInputCell";
import PillSelectCell from "./cellTypes/PillSelectCell";
import StringCell from "./cellTypes/StringCell";
import { StringWithIconCell } from "./cellTypes/StringWithIconCell";
import ThinkingButtonCell from "./cellTypes/ThinkingButtonCell";

const ActionableInputCell = React.lazy(
  () => import("./cellTypes/ActionableInputCell"),
);
const TagWithThreeDotsIconCell = React.lazy(
  () => import("./cellTypes/TagWithThreeDotsIconCell"),
);
const TagWithDropDownMenuCell = React.lazy(
  () => import("./cellTypes/TagWithDropDownMenuCell"),
);
export const TableCell = (props) => {
  switch (props.type) {
    case TableCellType.STRING:
      return <StringCell value={props.value} />;
    case TableCellType.BUTTON_LINK:
      return (
        <ButtonLinkCell
          value={props.value}
          onReviewClick={props.onReviewClick}
        />
      );
    case TableCellType.BADGE_BUTTON:
      return (
        <BadgeButton value={props.value} onReviewClick={props.onReviewClick} />
      );
    case TableCellType.DATE:
      return <DateCell value={props.value} />;
    case TableCellType.BUTTON_WITH_THREE_DOTS:
      return (
        <ButtonWithThreeDotsCell
          value={props.value}
          onThreeDotsOptionClick={props.onButtonWithThreeDotsOptionClick}
        />
      );
    case TableCellType.CMM_INPUT_VIEW_ICON:
      return <CmmInputViewIconCell value={props.value} />;
    case TableCellType.NOTES_ICON:
      return <NotesIconCell />;
    case TableCellType.MULTILINE:
      return (
        <MultilineCell
          value={props.value}
          shouldShowLeftBorder={props.value?.shouldShowLeftBorder}
          leftBorderColor={props.value?.leftBorderColor}
        />
      );

    case TableCellType.TOGGLE_SWITCH:
      return props.value.disabled ? (
        <div
          className="pointer-events-none opacity-40"
          title="This field is used in a QueryBuilder rule and cannot be disabled"
        >
          <ToggleSwitch
            id={props.value.id}
            defaultChecked={props.value.defaultChecked}
            onChangeEmit={() => {}}
          />
        </div>
      ) : (
        <ToggleSwitch
          id={props.value.id}
          defaultChecked={props.value.defaultChecked}
          onChangeEmit={props.onChangeEmit}
        />
      );
    case TableCellType.STRING_WITH_ICON:
      return <StringWithIconCell value={props.value} />;
    case TableCellType.COLORED_TEXT:
      return <ColoredTextCell value={props.value} />;
    case TableCellType.BADGE:
      return <BadgeCell value={props.value} />;
    case TableCellType.COPY_DATA:
      return <CopyDataCell value={props.value} />;
    case TableCellType.CALLING_RESOLVED_PHONE:
      return <CallingResolvedPhoneCell value={props.value} />;
    case TableCellType.DOTS_WITH_POPUP:
      return <DotsWithPopupCell value={props.value} />;
    case TableCellType.COPY_ICON:
      return <CopyIconCell value={props.value} />;
    case TableCellType.NYCBS_STATUS:
      return (
        <NycbsStatusCell
          value={props.value}
          handleStatusClick={props.handleStatusClick}
        />
      );
    case TableCellType.CHECKBOX:
      return (
        <CheckboxCell
          value={props.value}
          toggleCheckboxSelection={props.toggleCheckboxSelection}
        />
      );
    case TableCellType.THINKING_BUTTON:
      return <ThinkingButtonCell value={props.value} />;
    case TableCellType.ADD_KEY_BUTTON:
      return (
        <AddKeyButtonCell
          value={props.value?.value}
          isDisabled={props.value?.isDisabled}
          title={props.value?.title}
          isSftp={props.value?.isSftp}
          sftpStatus={props.value?.sftpStatus}
        />
      );
    case TableCellType.EDITABLE_TEXT:
      return (
        <EditableTextCellComponent
          value={props.value}
          onValueChange={props.onValueChange ?? (() => {})}
          header={props.header}
          rowIndex={props.rowIndex}
          onTableRowUpdate={props.onTableRowUpdate}
        />
      );
    case TableCellType.EXPANDABLE_ROW_ICON:
      return (
        <ExpandableRowIconCell
          value={props.value}
          onRowExpandChange={props.onRowExpandChange}
        />
      );
    case TableCellType.DOWNLOAD_ICON:
      return (
        <DownloadIconCell
          value={props.value}
          onDownloadIconClick={props.onDownloadIconClick}
        />
      );
    case TableCellType.ACTIONABLE_INPUT:
      return (
        <Suspense
          fallback={
            <>
              <SpinningLoader />
            </>
          }
        >
          <ActionableInputCell
            value={props.value}
            onActionOnInputClick={props.onActionOnInputClick}
            onInputChange={props.onInputChange}
          />
        </Suspense>
      );
    case TableCellType.TAG_WITH_THREE_DOTS:
      return (
        <Suspense fallback={<SpinningLoader />}>
          <TagWithThreeDotsIconCell
            value={props.value}
            onTagOptionClick={props.onTagOptionClick}
          />
        </Suspense>
      );
    case TableCellType.TAG_WITH_DROPDOWN_MENU:
      return (
        <Suspense fallback={<SpinningLoader />}>
          <TagWithDropDownMenuCell
            value={props.value}
            onTagDropDownSelectChange={props.onTagDropDownSelectChange}
          />
        </Suspense>
      );

    case TableCellType.BADGE_GROUP_WITH_EDIT_ICON:
      return (
        <BadgeGroupWithEditIconCell
          value={props.value}
          rowData={props.rowData}
          handleEditIconClick={props.handleEditIconClick}
        />
      );
    case TableCellType.SELECTABLE_BADGE:
      return (
        <BadgeSelectCell
          value={props.value}
          rowData={props.rowData}
          header={props.header}
          onCellValueChange={props.onCellValueChange}
        />
      );
    case TableCellType.PILL_SELECT:
      return (
        <PillSelectCell
          value={props.value}
          rowData={props.rowData}
          header={props.header}
          onCellValueChange={props.onCellValueChange}
        />
      );
    case TableCellType.EDITABLE_TAG_LIST:
      return (
        <EditableTagListCell
          value={props.value}
          rowData={props.rowData}
          header={props.header}
          onCellValueChange={props.onCellValueChange}
        />
      );
    case TableCellType.INLINE_TEXT_INPUT:
      return (
        <InlineTextInputCell
          value={props.value}
          rowData={props.rowData}
          header={props.header}
          onCellValueChange={props.onCellValueChange}
        />
      );
    case TableCellType.EDITABLE_TEXT_WITH_ICON:
      return (
        <EditableTextWithIconCell
          value={props.value}
          rowData={props.rowData}
          header={props.header}
          onCellValueChange={props.onCellValueChange}
        />
      );
    default:
      return <></>;
  }
};

TableCell.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any.isRequired,
  header: PropTypes.string,
  rowIndex: PropTypes.number,
  globalRowIndex: PropTypes.number,
  rowData: PropTypes.any,
  onReviewClick: PropTypes.func,
  handleStatusClick: PropTypes.func,
  handleDeleteIconClick: PropTypes.func,
  handleEditIconClick: PropTypes.func,
  handleClickableTextWithDisabledStatusClick: PropTypes.func,
  onComplete: PropTypes.func,
  toggleCheckboxSelection: PropTypes.func,
  onRowExpandChange: PropTypes.func,
  onButtonWithThreeDotsOptionClick: PropTypes.func,
  onDownloadIconClick: PropTypes.func,
  onActionOnInputClick: PropTypes.func,
  onInputChange: PropTypes.func,
  onTagOptionClick: PropTypes.func,
  onTagDropDownSelectChange: PropTypes.func,
  onCellValueChange: PropTypes.func,
};
